/**
 * Maneuver dials for ally ships.
 *
 * Allies aren't AI-driven (players plan their dial each round per the
 * Slicer Techs rule in defection1), so they have no entry in
 * `fgaManeuvers` / `andersonManeuvers`. We still want the squad card to
 * show the player what the ship's dial physically allows — this map
 * provides that per-ship list.
 *
 * Convention: only the centre + right-hand maneuvers are listed. The
 * dial renderer mirrors them visually (or, per current spec, just notes
 * that the left side mirrors the right) so we don't duplicate data.
 */

import type { ShipId } from './Ships';

export type DialDifficulty = 'white' | 'red' | 'blue';
export type DialDirection =
  | 'straight'
  | 'bank'
  | 'turn'
  | 'kturn'
  | 'sloop'
  | 'troll'
  | 'stop'
  | 'reverseStraight'
  | 'reverseBank';

export interface DialEntry {
  speed: number;
  direction: DialDirection;
  difficulty: DialDifficulty;
  /**
   * Energy generated by executing this maneuver (huge ships only — the GR-75
   * "charges" energy on slower moves). Shown as a small `+N` badge on the
   * dial cell. HotAC missions override the economy with their own "gains N
   * energy instead of moving" rules; these are the printed-dial values.
   */
  energy?: number;
}

// HWK-290 dial (HotAC variant):
//   0: stationary (red)
//   1: straight blue, bank blue
//   2: straight blue, bank white, turn white
//   3: straight blue, bank white, turn red
//   4: straight white
const hwk290: readonly DialEntry[] = [
  { speed: 0, direction: 'stop', difficulty: 'red' },
  { speed: 1, direction: 'straight', difficulty: 'blue' },
  { speed: 1, direction: 'bank', difficulty: 'blue' },
  { speed: 2, direction: 'straight', difficulty: 'blue' },
  { speed: 2, direction: 'bank', difficulty: 'white' },
  { speed: 2, direction: 'turn', difficulty: 'white' },
  { speed: 3, direction: 'straight', difficulty: 'blue' },
  { speed: 3, direction: 'bank', difficulty: 'white' },
  { speed: 3, direction: 'turn', difficulty: 'red' },
  { speed: 4, direction: 'straight', difficulty: 'white' },
];

// Outer Rim Smuggler — the X-Wing 2nd-edition "Modified YT-1300" dial.
//   1: straight blue, bank blue
//   2: straight blue, bank blue, turn white
//   3: straight blue, bank white, turn white, Segnor's loop red
//   4: straight white, Koiogran turn red
// (Centre + right only; the renderer mirrors the left side.)
const outerRimSmuggler: readonly DialEntry[] = [
  { speed: 1, direction: 'straight', difficulty: 'blue' },
  { speed: 1, direction: 'bank', difficulty: 'blue' },
  { speed: 2, direction: 'straight', difficulty: 'blue' },
  { speed: 2, direction: 'bank', difficulty: 'blue' },
  { speed: 2, direction: 'turn', difficulty: 'white' },
  { speed: 3, direction: 'straight', difficulty: 'blue' },
  { speed: 3, direction: 'bank', difficulty: 'white' },
  { speed: 3, direction: 'turn', difficulty: 'white' },
  { speed: 3, direction: 'sloop', difficulty: 'red' },
  { speed: 4, direction: 'straight', difficulty: 'white' },
  { speed: 4, direction: 'kturn', difficulty: 'red' },
];

// GR-75 huge-ship dial (same in 1st & 2nd edition). All white, banks +
// straights only — no turns / stop / k-turn. Slower maneuvers charge more
// energy; the `energy` field carries the per-maneuver yield.
const gr75: readonly DialEntry[] = [
  { speed: 1, direction: 'straight', difficulty: 'white', energy: 3 },
  { speed: 1, direction: 'bank', difficulty: 'white', energy: 2 },
  { speed: 2, direction: 'straight', difficulty: 'white', energy: 2 },
  { speed: 2, direction: 'bank', difficulty: 'white', energy: 1 },
  { speed: 3, direction: 'straight', difficulty: 'white', energy: 1 },
  { speed: 4, direction: 'straight', difficulty: 'white', energy: 0 },
];

// TIE Defender (HotAC Defector ally variant). Per user spec — centre +
// right only; the renderer mirrors the left side.
//   1: turn (red), bank (blue)
//   2: turn (red), bank (white), straight (blue), Koiogran turn (red)
//   3: turn (white), bank (white), straight (blue)
//   4: straight (blue), Koiogran turn (white)
//   5: straight (blue)
const tiedefAlly: readonly DialEntry[] = [
  { speed: 1, direction: 'turn', difficulty: 'red' },
  { speed: 1, direction: 'bank', difficulty: 'blue' },
  { speed: 2, direction: 'turn', difficulty: 'red' },
  { speed: 2, direction: 'bank', difficulty: 'white' },
  { speed: 2, direction: 'straight', difficulty: 'blue' },
  { speed: 2, direction: 'kturn', difficulty: 'red' },
  { speed: 3, direction: 'turn', difficulty: 'white' },
  { speed: 3, direction: 'bank', difficulty: 'white' },
  { speed: 3, direction: 'straight', difficulty: 'blue' },
  { speed: 4, direction: 'straight', difficulty: 'blue' },
  { speed: 4, direction: 'kturn', difficulty: 'white' },
  { speed: 5, direction: 'straight', difficulty: 'blue' },
];

export const ALLY_DIALS: Readonly<Partial<Record<ShipId, readonly DialEntry[]>>> =
  Object.freeze({
    HWK290: hwk290,
    OUTER_RIM_SMUGGLER: outerRimSmuggler,
    GR75: gr75,
    TIEDEF: tiedefAlly,
  });

/**
 * Mission modifier that recolours dial entries. A rule matches entries by
 * speed and/or direction (omit a field to match any) and sets their
 * difficulty. General by design: "Speed-4 maneuvers are treated as red" on
 * the damaged HWK-290 is `{ speed: 4, difficulty: 'red' }` — no per-quirk
 * boolean flags.
 */
export interface DialMod {
  speed?: number;
  direction?: DialDirection;
  difficulty: DialDifficulty;
}

/** Apply mission dial modifiers to a base dial, returning a new array. */
export function applyDialMods(
  dial: readonly DialEntry[],
  mods: readonly DialMod[] = [],
): readonly DialEntry[] {
  if (mods.length === 0) return dial;
  return dial.map((entry) => {
    const mod = mods.find(
      (m) =>
        (m.speed === undefined || m.speed === entry.speed) &&
        (m.direction === undefined || m.direction === entry.direction),
    );
    return mod ? { ...entry, difficulty: mod.difficulty } : entry;
  });
}

/** Resolve an ally's dial: the ship-type's base dial with mission mods applied. */
export function resolveAllyDial(
  shipType: ShipId,
  mods: readonly DialMod[] = [],
): readonly DialEntry[] {
  return applyDialMods(ALLY_DIALS[shipType] ?? [], mods);
}
